// Server-backed geo data for the active object set. Built entirely on the
// backend's `_geo_point` projection (no client-side `classify` derivation),
// always scoped to the object set's node ids.
//
// Two sources, one shared server engine — dispatched by the object set's state:
//   - saved + clean  → the precomputed `investigation.geo` stored at save/sync,
//                      read straight from the loaded investigation (no call).
//   - unsaved/dirty  → a real-time `POST /geo/project { node_ids }` over the
//     (transient,       current object set (transient view, or a saved
//      or explored)     investigation with unsaved exploration).
//
// Either source yields the same `{anchors, tracks, events}` blob, adapted into
// the shared `GeoData` shape:
//   - anchors → static pins   - tracks → per-entity tracks   - events → timeline

import { useQuery } from '@tanstack/react-query'
import { useMemo } from 'react'
import { useParams } from 'react-router-dom'
import { snippetNodeId, snippetSelection } from '@/features/source-snippets/object-set'
import { mergeSnippetGeo } from '@/features/source-snippets/projections'
import { useSourceSnippets } from '@/features/source-snippets/useSourceSnippets'
import { investigationsApi } from '@/services/api/investigations'
import type { GeoEntity, TrackPoint } from '@/stores/geolocation-store'
import { useInvestigationsStore } from '@/stores/investigations-store-provider'
import type { TimelineEvent } from '@/stores/timeline-store'
import type { InvestigationGeo } from '@/types/investigation'

export interface ServerGeoData {
  entities: GeoEntity[]
  tracks: Record<string, TrackPoint[]>
  globalTimeRange: { start: string; end: string } | null
  temporalEvents: TimelineEvent[]
  keyToNode: Record<string, string>
  nodeToKey: Record<string, string>
  hasActiveInvestigation: boolean
  /** Retained for API compatibility; the projection is read synchronously
   *  from the loaded investigation, so this is always false here. */
  isLoading: boolean
  /** True when this source owns the data (a saved investigation is active). */
  enabled: boolean
}

const EMPTY: ServerGeoData = {
  entities: [],
  tracks: {},
  globalTimeRange: null,
  temporalEvents: [],
  keyToNode: {},
  nodeToKey: {},
  hasActiveInvestigation: false,
  isLoading: false,
  enabled: false,
}

export function useServerInvestigationGeoData(): ServerGeoData {
  const { caseId } = useParams<{ caseId: string }>()
  const snippets = useSourceSnippets(caseId ?? '')
  const activeId = useInvestigationsStore(s => s.activeId)
  const transient = useInvestigationsStore(s => s.transient)
  const saved = useInvestigationsStore(s => s.saved)
  const dirty = useInvestigationsStore(s => s.dirty)

  // The active object set + whether it's the clean saved state. The object set
  // is the transient view's nodes, else the active saved investigation's nodes
  // (which already include any unsaved exploration additions).
  const activeInv = activeId ? saved.find(i => i.id === activeId) : undefined
  const objectSetNodeIds = useMemo(() => {
    const nodes = transient?.graphView?.nodes ?? activeInv?.graphView?.nodes ?? []
    return nodes.map(n => n.id).filter(Boolean)
  }, [transient, activeInv])

  const enabled = objectSetNodeIds.length > 0 || snippets.length > 0

  // Saved + clean reads the precomputed projection stored on the investigation.
  const storedGeo: InvestigationGeo | null =
    !transient && activeInv && !dirty ? (activeInv.geo ?? null) : null

  // Anything unsaved/dirty (or a saved investigation whose geo predates this
  // feature) projects the current object set live over the same engine.
  const needLive = objectSetNodeIds.length > 0 && storedGeo == null
  const liveKey = useMemo(() => [...objectSetNodeIds].sort().join(','), [objectSetNodeIds])
  const liveQ = useQuery<InvestigationGeo>({
    queryKey: ['geo-project', caseId, liveKey],
    queryFn: () => investigationsApi.projectGeo(caseId as string, objectSetNodeIds),
    enabled: needLive && Boolean(caseId),
    staleTime: 30_000,
  })

  const baseGeo = storedGeo ?? liveQ.data ?? null
  const geo = useMemo(() => mergeSnippetGeo(baseGeo, snippets), [baseGeo, snippets])

  return useMemo(() => {
    if (!enabled) return EMPTY

    const entities: GeoEntity[] = []
    const tracks: Record<string, TrackPoint[]> = {}
    const temporalEvents: TimelineEvent[] = []
    const keyToNode: Record<string, string> = {}
    const nodeToKey: Record<string, string> = {}
    let earliest: string | null = null
    let latest: string | null = null
    const considerTs = (ts: string) => {
      if (!ts) return
      if (!earliest || ts < earliest) earliest = ts
      if (!latest || ts > latest) latest = ts
    }
    const mapKey = (id: string) => {
      const snippet = snippetSelection(id)
      const memberId = snippet ? snippetNodeId(snippet.id) : id
      keyToNode[id] = memberId
      nodeToKey[memberId] ??= id
    }

    for (const a of geo?.anchors ?? []) {
      mapKey(a.anchor_id)
      entities.push({
        id: a.anchor_id,
        label: a.type ?? undefined,
        name: a.name,
        type: (a.type ?? 'unknown').toLowerCase(),
        latitude: a.latitude,
        longitude: a.longitude,
        isStatic: !a.temporal,
        // Non-trackable entity with timed observations → time-anchored pin
        // (the scrubber filters it); otherwise an always-visible static pin.
        mapRenderMode: a.temporal ? 'time_anchored_pin' : 'static_pin',
        dimmed: a.dimmed ?? false,
        properties: {
          entity_id: a.anchor_id,
          name: a.name,
          ...(a.via_anchor_id ? { _via_anchor_id: a.via_anchor_id } : {}),
          // Temporal extent so the timeline can place + filter a time-anchored
          // pin (read by readTemporalExtent as `_temporal_min`/`_temporal_max`).
          ...(a.temporal
            ? { _temporal_min: a.temporal_min ?? null, _temporal_max: a.temporal_max ?? null }
            : {}),
        },
      })
    }

    for (const tr of geo?.tracks ?? []) {
      const points: TrackPoint[] = tr.points.map(p => ({
        latitude: p.latitude,
        longitude: p.longitude,
        timestamp: p.timestamp,
      }))
      if (points.length === 0) continue
      tracks[tr.entity_id] = points
      mapKey(tr.entity_id)
      for (const p of points) considerTs(p.timestamp)
      const first = points[0]
      entities.push({
        id: tr.entity_id,
        label: tr.type ?? undefined,
        name: tr.name,
        type: (tr.type ?? 'unknown').toLowerCase(),
        latitude: first.latitude,
        longitude: first.longitude,
        isStatic: false,
        mapRenderMode: 'animated_track',
        properties: { entity_id: tr.entity_id, name: tr.name },
      })
    }

    const events = geo?.events ?? []
    events.forEach((e, i) => {
      considerTs(e.timestamp)
      temporalEvents.push({
        id: `${e.entity_id}@${e.timestamp}#${i}`,
        type: e.type ?? 'Unknown',
        timestamp: e.timestamp,
        sourceId: e.entity_id,
        sourceName: e.entity_name,
        targetId: '',
        targetName: '',
        properties: {
          latitude: e.latitude,
          longitude: e.longitude,
          location_name: e.location_name ?? null,
        },
      })
    })

    temporalEvents.sort((x, y) => x.timestamp.localeCompare(y.timestamp))

    return {
      entities,
      tracks,
      globalTimeRange: earliest && latest ? { start: earliest, end: latest } : null,
      temporalEvents,
      keyToNode,
      nodeToKey,
      hasActiveInvestigation: true,
      isLoading: needLive && liveQ.isLoading,
      enabled: true,
    }
  }, [enabled, geo, needLive, liveQ.isLoading])
}

/**
 * Single geo-data source for the map. Server `_geo_point` projection for the
 * active object set — stored when saved + clean, real-time `/geo/project`
 * otherwise. No client-side derivation.
 */
export function useGeoMapData(): ServerGeoData {
  return useServerInvestigationGeoData()
}
